// BentoSelect — the React port of the legacy custom <select> picker (Phase 3a).
//
// The legacy app (main.js "Dropdown picker" block) replaces every native
// dropdown with a custom UI: a fixed popover on desktop and for the global
// top-bar selects, and an inline accordion for a select inside a placed module
// on mobile. This component reproduces that behaviour and — critically — emits
// the *same class names and DOM* the legacy picker produced, so the shared
// CSS (the design contract) styles it identically:
//   popover  →  .desktop-picker.open > .desktop-picker-list > .desktop-picker-option[.selected]
//   inline   →  .mobile-inline-picker > .mobile-inline-picker-option[.selected]
//   trigger  →  <select class="module-controls …"> (the styled control itself)
//
// It is a controlled component: `value` in, `onChange(value)` out. It does NOT
// know about the store or module wiring — consumers decide what a change means.
//
// Coexistence: while renderers are still legacy `innerHTML`, their selects keep
// being handled by the legacy interceptor. BentoSelect is the path forward that
// converted components use; the legacy IIFE stays until nothing needs it.
//
// Accessibility note: the legacy picker had no keyboard/ARIA support. We keep
// the native <select> as the trigger, so native keyboard selection still works
// and updates state; richer ARIA for the custom list is a deliberate follow-up
// (kept out of this PR to hold "same DOM, no visual change").

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  selectPickerMode,
  popoverPlacement,
  clampPopoverLeft,
  type PickerMode,
  type PopoverPlacement,
} from "@bento/shared";

export interface BentoSelectOption {
  value: string;
  label: string;
}

export interface BentoSelectProps {
  value: string;
  options: BentoSelectOption[];
  onChange: (value: string) => void;
  /**
   * Extra class(es) on the <select> trigger, e.g. "timeline-toggle". The shared
   * "module-controls" class (which the picker and CSS key on) is always applied.
   */
  className?: string;
  /** Accessible name for the trigger. */
  ariaLabel?: string;
}

/** Matches the legacy `isMobile()` breakpoint. */
function isMobileViewport(): boolean {
  return window.innerWidth <= 768;
}

export default function BentoSelect({
  value,
  options,
  onChange,
  className,
  ariaLabel,
}: BentoSelectProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PickerMode>("popover");
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);

  // Latest props for the imperative (inline) row handlers: the inline accordion
  // is built once on open, so its closures must read current props, not stale
  // ones, if the parent re-renders while the picker is up.
  const latest = useRef({ options, value, onChange });
  latest.current = { options, value, onChange };

  function openPicker() {
    const el = selectRef.current;
    if (!el) return;
    const nextMode = selectPickerMode(
      isMobileViewport(),
      !!el.closest(".placed-module")
    );
    if (nextMode === "popover") {
      setPlacement(popoverPlacement(el.getBoundingClientRect()));
    }
    setMode(nextMode);
    setOpen(true);
  }

  function onTriggerPointerDown(e: ReactPointerEvent<HTMLSelectElement>) {
    // Suppress the native dropdown; the custom picker takes over. Tapping the
    // same select again while open closes it (legacy toggle behaviour).
    e.preventDefault();
    if (open) setOpen(false);
    else openPicker();
  }

  // Outside-click close, capture phase — same as the legacy interceptor. Only
  // the popover is in React's tree; the inline host is imperative, so both refs
  // are checked.
  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (selectRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      if (inlineHostRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [open]);

  // Popover overflow correction: once mounted at its real width, nudge left if
  // it would spill past the right edge. Mirrors the legacy rAF clamp; converges
  // in one extra pass (the clamp is idempotent).
  useLayoutEffect(() => {
    if (!open || mode !== "popover" || !placement || !popoverRef.current) return;
    const width = popoverRef.current.getBoundingClientRect().width;
    const left = clampPopoverLeft(placement.left, width, window.innerWidth);
    if (left !== placement.left) setPlacement({ ...placement, left });
  }, [open, mode, placement]);

  // Inline accordion (mobile, inside a placed module). Built imperatively: it
  // inserts a sibling after .module-sub-controls that overlays .module-body —
  // it reaches out into the still-legacy module shell. Faithful to the legacy openInlinePicker/closeInlinePicker; Phase 4
  // folds it into JSX once the shell itself is React.
  const inlineHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open || mode !== "inline") return;
    const el = selectRef.current;
    const found = el?.closest<HTMLElement>(".placed-module");
    const subControls = found?.querySelector<HTMLElement>(
      ".module-sub-controls"
    );
    if (!found || !subControls) return;
    // Alias to a non-null const so the narrowing survives into the deferred
    // cleanup closure below.
    const mod: HTMLElement = found;

    const host = document.createElement("div");
    host.className = "mobile-inline-picker";
    for (const opt of latest.current.options) {
      const row = document.createElement("div");
      row.className =
        "mobile-inline-picker-option" +
        (opt.value === latest.current.value ? " selected" : "");
      row.textContent = opt.label;
      row.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        latest.current.onChange(opt.value);
        setOpen(false);
      });
      host.appendChild(row);
    }

    // Overlay the module body (the module never changes size): sit just
    // below the controls, no taller than the space left in the module (scrolls
    // past that). Insert collapsed, then open via max-height.
    host.style.maxHeight = "0";
    subControls.after(host);
    inlineHostRef.current = host;
    const top =
      subControls.getBoundingClientRect().bottom - mod.getBoundingClientRect().top - mod.clientTop;
    host.style.top = top + "px";
    const openH = Math.min(host.scrollHeight, mod.clientHeight - top);
    const raf = requestAnimationFrame(() => {
      host.style.maxHeight = openH + "px";
    });

    return () => {
      cancelAnimationFrame(raf);
      inlineHostRef.current = null;
      host.style.maxHeight = "0";
      setTimeout(() => host.remove(), 300); // after the 0.28s collapse
    };
  }, [open, mode]);

  return (
    <>
      <select
        ref={selectRef}
        className={`module-controls${className ? " " + className : ""}`}
        value={value}
        aria-label={ariaLabel}
        onPointerDown={onTriggerPointerDown}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {open &&
        mode === "popover" &&
        placement &&
        createPortal(
          <div
            ref={popoverRef}
            className="desktop-picker open"
            style={{
              left: placement.left,
              top: placement.top,
              minWidth: placement.minWidth,
            }}
          >
            <div className="desktop-picker-list">
              {options.map((opt) => (
                <div
                  key={opt.value}
                  className={
                    "desktop-picker-option" +
                    (opt.value === value ? " selected" : "")
                  }
                  onPointerDown={(e) => {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
